/**
 * Turning a remote-control keypress into a `KeyData` the focus graph can act on.
 *
 * ★ PARITY SOURCE: `core/app/js/app.util.js` → `app.util.identifyKey` (v1). The rule for TVv2 is
 * that the CURRENT TV APP IS THE SPEC — so this reproduces v1's classification, including the two
 * places where being faithful means deliberately NOT improving things (see below).
 *
 * ★ AND WHERE THIS RUNS. On a television the HOST owns the only real `keydown` listener; it
 * identifies the key and bridges the resulting `KeyData` to whichever guest is showing. So the guest
 * usually receives a `KeyData` off the wire, already classified, and never sees a `KeyboardEvent` at
 * all. `identifyKey` is for the host — and for a guest opened directly in a browser, which is how the
 * TV app is developed and how its golden-reference baseline is captured.
 */

export type KeyType = 'arrow' | 'control' | 'alpha' | 'num' | 'punc' | 'function' | 'unknown'

export interface KeyData {
  code: string | null
  key: string | null
  type: KeyType
  /**
   * ★ THE PLATFORM'S OWN AUTO-REPEAT, AND IT IS THE DISCRIMINATOR HOLD-TO-REPEAT NEEDS. A held key
   * produces a stream of keydowns at whatever rate the browser chooses; `KeyboardEvent.repeat` is
   * true for every one of them after the first, and false for a genuine new press. Without it the two
   * are indistinguishable, and a viewer tapping `10▶` five times quickly would be regulated down to
   * the hold cadence — or, the other way round, a hold would run at thirty seeks a second.
   *
   * ⚠️ Optional because it crosses the bridge as data and an old TV browser may not set it. Absent it
   * reads as "a new press", which is today's behaviour: no regulation, nothing worse.
   */
  repeat?: boolean
}

/**
 * Old TV browsers (Tizen 4, early webOS) fire `keydown` with a `keyCode` and NEITHER `key` NOR
 * `code`. Without this map the whole remote is dead on those platforms.
 */
const LEGACY_KEYMAP: Record<number, string> = {
  8: 'Backspace',
  9: 'Tab',
  13: 'Enter',
  16: 'Shift',
  17: 'Control',
  18: 'Alt',
  19: 'Pause',
  20: 'CapsLock',
  27: 'Escape',
  32: 'Space',
  33: 'PageUp',
  34: 'PageDown',
  35: 'End',
  36: 'Home',
  37: 'ArrowLeft',
  38: 'ArrowUp',
  39: 'ArrowRight',
  40: 'ArrowDown',
  45: 'Insert',
  46: 'Delete',
}

/**
 * The remote's BACK button, per platform.
 *
 * ★ Android (8) and Fire OS (27) already arrive as Backspace/Escape through `LEGACY_KEYMAP`, and on
 * those two the NATIVE shell owns Back anyway (`MainActivity.kt` — `dispatchKeyEvent` plus an
 * `OnBackPressedCallback`). v1 had a clause for all four, and David deliberately disabled it by
 * misspelling its guard (`device.category === "tvx"`) — correctly, because it would have
 * double-handled Back on the only two platforms that ship. Reading it now, it is also simply broken:
 * `case 13` falls through into `case 37`, so Enter would have arrived as ArrowLeft.
 *
 * ★ What the dead clause DID contain, and what nothing else supplies, is webOS 461 and Tizen 10009.
 * Both are unshipped, so mapping them here changes no shipped behaviour — and phase 8 needs them.
 */
const BACK_KEYCODES: Record<number, string> = {
  461: 'Backspace', // webOS
  10009: 'Backspace', // Tizen
}

/**
 * ⚠ DELIBERATELY ABSENT: the media transport keys (play/pause, fast-forward, rewind).
 *
 * The focus graph HAS a vocabulary for them — `pp`, `ff`, `rw` — but v1's `identifyKey` classifies
 * `MediaPlayPause` and friends as `punc`, and the handler only consults `pp`/`ff`/`rw` under type
 * `control`. So on today's TVs those remote buttons **do nothing**, and the graph keys are dead
 * letters. Mapping them here would be an improvement, and improvements are exactly what the UX-parity
 * rule forbids without David's say-so. The vocabulary is kept; the wiring is his call.
 */

const isArrow = (key: string): boolean => /^Arrow(Left|Right|Up|Down)$/.test(key)

const CONTROL_KEYS = new Set([
  'Enter',
  'Escape',
  'Backspace',
  'Tab',
  'Delete',
  'Insert',
  'Home',
  'End',
  'PageUp',
  'PageDown',
])

function classify(key: string): KeyType {
  if (/^[a-zA-Z]$/.test(key)) return 'alpha'
  if (/^[0-9]$/.test(key)) return 'num'
  if (isArrow(key)) return 'arrow'
  if (/^F[0-9]{1,2}$/.test(key)) return 'function'
  if (CONTROL_KEYS.has(key)) return 'control'
  // ★ Space lands here — `punc`, not `control` — which is v1's classification and means SPACE DOES
  // NOT ACTIVATE, even though the key handler has a `case "Space"` beside `case "Enter"`. That case
  // is unreachable. Faithful, and harmless: no remote has a space bar.
  return 'punc'
}

export function identifyKey(e: KeyboardEvent): KeyData {
  const repeat = e.repeat === true
  const fromBack = BACK_KEYCODES[e.keyCode]
  if (fromBack) return { code: fromBack, key: fromBack, type: 'control', repeat }

  let key = e.key || null
  let code = e.code || null

  // No `key` and no `code`: an old TV browser. Rebuild both from the keyCode.
  if (!key && !code && typeof e.keyCode !== 'undefined') {
    const kc = e.keyCode
    if (kc >= 65 && kc <= 90) {
      key = String.fromCharCode(kc).toLowerCase()
      code = 'Key' + String.fromCharCode(kc)
    } else if (kc >= 48 && kc <= 57) {
      key = String.fromCharCode(kc)
      code = 'Digit' + key
    } else {
      key = LEGACY_KEYMAP[kc] ?? String.fromCharCode(kc)
      code = LEGACY_KEYMAP[kc] ?? null
    }
  }

  if (key === ' ') key = 'Space'
  if (!key) return { code, key: null, type: 'unknown', repeat }

  // Letter case follows the modifier state, so the soft keyboard can echo what was actually typed.
  if (code && /^Key[A-Z]$/.test(code)) {
    const caps = typeof e.getModifierState === 'function' && e.getModifierState('CapsLock')
    const letter = code.replace('Key', '')
    key = (e.shiftKey && !caps) || (!e.shiftKey && caps) ? letter.toUpperCase() : letter.toLowerCase()
  }
  if (code && /^Digit[0-9]$/.test(code)) key = code.replace('Digit', '')
  else if (code && /^Numpad[0-9]$/.test(code)) key = code.replace('Numpad', '')

  return { code, key, type: classify(key), repeat }
}

/** The Enter the app synthesises for itself — `enterOnFocus`, and a mouse click on a TV. */
export const SIMULATED_ENTER: KeyData = { code: 'Enter', key: 'Enter', type: 'control' }
